import React, { useContext } from "react";

import LaunchIcon from "@mui/icons-material/Launch";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import { Tier } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/common_pb";
import { ContractStatus } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/contracts_pb";
import { PaymentCollectionMethod } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/payment_method_pb";
import { SubscriptionState } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/subscriptions_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";

import { ENTERPRISE_PLAN_DOC_LINK } from "../../../../constants/contracts";
import { NO_DISPLAY_DATA } from "../../../../constants/index";
import { toUUID } from "../../../../helpers/s12-helpers";
import {
  convertTimestampToDate,
  formatDateDisplayOmittingMidnight,
} from "../../../../helpers/time";
import mainStyles from "../../../../styles";
import BillingContactsForm from "../../details-display-cards/billing-contacts-form";
import BillingDetailsForm from "../../details-display-cards/billing-details-form";
import ChangeComponentQuantityZuora from "../../tabs/subscription-actions/change-component-quantity-zuora";
import { CardShell, Field, FieldGrid, IdValue, InfoHint } from "./card-shell";
import { SeatMeter, StatusCell } from "./status-pill";
import {
  FEATURE_FLAG_KEY_DISABLE_BILLING_UPDATES,
  FEATURE_FLAG_KEY_MANUAL_BILLING_EXPERIENCE,
} from "constants/billing-experiences";
import { userSeatsToString } from "constants/seats";
import {
  FeatureFlagsContext,
  UseGetFeatureFlagValue,
} from "context/feature-flags-context";
import {
  ContractsListContext,
  OrgDetailsContext,
  SubDataContext,
  SubscriptionSeatUsageContext,
} from "context/organisation-context";
import { PermissionsContext } from "context/permissions-context";
import { SnackbarContext } from "context/snackbar-context";
import useBillingDetails from "helpers/hooks/use-billing-details";
import { deriveProductStatusForZuora } from "helpers/product-status";
import { getPaymentStatus } from "helpers/raw-subscription-doc";
import { getZuoraSubscriptionUrl } from "helpers/subscriptions";

/**
 * What each status field means, and what each of its values means. Support and
 * CS read these fields constantly and the codes are not self-explanatory, so
 * the explanation travels with the value rather than living in a runbook.
 */
const TIER_HELP =
  "The plan level the organisation sits on. An activated enterprise contract overrides whatever the subscription record says.";
const PRODUCT_HELP =
  "Derived by Periscope from contract and subscription data: whether the org can use the product right now.";
const SUBSCRIPTION_HELP =
  "The state of the subscription record as billing sees it — it describes the agreement, not whether anyone can log in.";
/** Same wording the pane uses for values read from the raw Zuora document. */
const SUBSCRIPTION_DOC_HINT = "Data from Subscription Document";

const PAYMENT_HELP =
  "How the org is tracking against its invoices, and how far collection has escalated.";

/**
 * A free org has no Zuora side at all, so its billing fields are not empty by
 * accident - there is nothing to put in them. The detail panes leave those rows
 * out entirely and the cards do the same, rather than showing a column of N/A.
 */
const isFreeTier = (subscription?: { tier?: Tier }) =>
  subscription?.tier === Tier.FREE;

/** Heading for a section inside a card, quieter than the card's own title. */
const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography
    sx={{
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.09em",
      textTransform: "uppercase",
      color: "text.disabled",
      mb: 0.5,
    }}
  >
    {children}
  </Typography>
);

/**
 * A date range on one line.
 *
 * Monospaced so the two ranges line up digit for digit down the card, and
 * slightly tighter than the body text so "01 Jul 2026 - 01 Jul 2027" survives
 * a half-width field without wrapping.
 */
const DateRange = ({ children }: { children: React.ReactNode }) => (
  <Box
    component="span"
    sx={{
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      fontSize: 12,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </Box>
);

const formatDate = (
  timestamp?: Parameters<typeof convertTimestampToDate>[0],
) =>
  timestamp
    ? formatDateDisplayOmittingMidnight(convertTimestampToDate(timestamp))
    : undefined;

export const PlanStatusCard = () => {
  const classes = mainStyles();
  const { subData } = useContext(SubDataContext);
  const { copyToClipboard } = useContext(SnackbarContext);
  const { orgDetails } = useContext(OrgDetailsContext);
  const { contractsList, isContractListLoading, contractListError } =
    useContext(ContractsListContext);

  const subscription = subData?.subscription;

  const isOrgOnEnterprisePlan = Boolean(
    contractsList?.contracts?.some(
      (contract) =>
        contract?.status === ContractStatus.ACTIVATED &&
        contract?.hasEnterprise === true,
    ),
  );
  const tier =
    subscription && isOrgOnEnterprisePlan
      ? Tier.ENTERPRISE
      : subscription?.tier;

  const tierDisplay: Record<number, { label: string; style: object }> = {
    [Tier.UNSPECIFIED]: { label: "Unspecified", style: classes.purpleRain },
    [Tier.FREE]: { label: "Free", style: classes.neutral },
    [Tier.PREMIUM]: { label: "Premium", style: classes.purpleRain },
    [Tier.ENTERPRISE]: { label: "Enterprise", style: classes.redWine },
  };

  const productStatus = deriveProductStatusForZuora(
    orgDetails?.active,
    subscription?.tier,
    contractsList?.contracts,
    isContractListLoading,
    Boolean(contractListError),
  );
  const productStyle: Record<string, object> = {
    Active: classes.greenDay,
    "Active (Trialing)": classes.blueMoon,
    Suspended: classes.redWine,
    Inactive: classes.neutral,
  };

  const subscriptionStateStyle: Record<number, object> = {
    [SubscriptionState.UNSPECIFIED]: classes.neutral,
    [SubscriptionState.ACTIVE]: classes.greenDay,
    [SubscriptionState.TRIALING]: classes.blueMoon,
    [SubscriptionState.PAST_DUE]: classes.yellowSubmarine,
    [SubscriptionState.CANCELED]: classes.redWine,
  };
  const subscriptionStateLabel: Record<number, string> = {
    [SubscriptionState.UNSPECIFIED]: "Unspecified",
    [SubscriptionState.ACTIVE]: "Active",
    [SubscriptionState.TRIALING]: "Trialing",
    [SubscriptionState.PAST_DUE]: "Past Due",
    [SubscriptionState.CANCELED]: "Canceled",
  };

  const paymentStatus = getPaymentStatus(subData?.json);
  const paymentStyle: Record<string, object> = {
    Paid: classes.greenDay,
    "Unpaid (Within Terms)": classes.yellowSubmarine,
    "Unpaid (Overdue - Dunning)": classes.orangeCrush,
    "Unpaid (Overdue - Grace Period)": classes.redWine,
  };

  const state = subscription?.state;
  const isFreeOrg = isFreeTier(subscription);
  const zuoraSubscriptionUrl = getZuoraSubscriptionUrl(subscription);

  const tierValues = [
    {
      label: "Free",
      style: classes.neutral,
      description: "No paid subscription; free plan limits apply.",
    },
    {
      label: "Premium",
      style: classes.purpleRain,
      description: "Paid self-serve or sales-assisted plan.",
    },
    {
      label: "Enterprise",
      style: classes.redWine,
      description:
        "Has an activated enterprise contract, which overrides the subscription record's tier.",
    },
    {
      label: "Unspecified",
      style: classes.purpleRain,
      description: "No tier on the record - usually worth raising.",
    },
  ];
  const productValues = [
    {
      label: "Active",
      style: classes.greenDay,
      description: "Paying and in good standing; full access.",
    },
    {
      label: "Active (Trialing)",
      style: classes.blueMoon,
      description: "Inside a trial period; nothing has been billed yet.",
    },
    {
      label: "Suspended",
      style: classes.redWine,
      description:
        "Access has been cut off - non-payment or a manual suspension.",
    },
    {
      label: "Inactive",
      style: classes.neutral,
      description: "No live subscription: cancelled, expired or never started.",
    },
  ];
  const subscriptionValues = [
    {
      label: "Active",
      style: classes.greenDay,
      description: "Live, billing on schedule.",
    },
    {
      label: "Trialing",
      style: classes.blueMoon,
      description: "In a trial; converts or expires at the trial end date.",
    },
    {
      label: "Past Due",
      style: classes.yellowSubmarine,
      description:
        "An invoice is unpaid past its due date. Still active - see Payment Status for how far collection has gone.",
    },
    {
      label: "Canceled",
      style: classes.redWine,
      description:
        "Cancelled; access may continue until the end of the paid period.",
    },
    {
      label: "Unspecified",
      style: classes.neutral,
      description: "No state on the record. Treat as a data issue.",
    },
  ];
  const paymentValues = [
    {
      label: "Paid",
      style: classes.greenDay,
      description: "No outstanding balance.",
    },
    {
      label: "Unpaid (Within Terms)",
      style: classes.yellowSubmarine,
      description: "An invoice is open but not yet due. No action needed.",
    },
    {
      label: "Unpaid (Overdue - Grace Period)",
      style: classes.redWine,
      description:
        "An item on the account is overdue, within 14 days of its due date.",
    },
    {
      label: "Unpaid (Overdue - Dunning)",
      style: classes.orangeCrush,
      description:
        "An item on the account is more than 14 days past its due date.",
    },
    {
      label: "Unknown",
      style: classes.neutral,
      description:
        "No payment status on the subscription document. Check the account directly in Zuora.",
    },
  ];

  return (
    <CardShell
      title="Plan &amp; status"
      source="Mixed"
      testId="dashboard-card-plan-status"
    >
      {/* No rules: the other cards are a plain grid of fields and this one is
          no different. Tier and Payment are a pill each; Product and
          Subscription each carry an identifier too, so they take two thirds of
          the row and the pairs still line up. */}
      <FieldGrid>
        <StatusCell
          label="Tier"
          help={TIER_HELP}
          value={tier === undefined ? NO_DISPLAY_DATA : tierDisplay[tier].label}
          chipStyle={
            tier === undefined ? classes.neutral : tierDisplay[tier].style
          }
          values={tierValues}
          note={{
            text: "Enterprise tier is calculated from the contracts, not the subscription: any contract with status Activated and hasEnterprise true wins.",
            linkHref: ENTERPRISE_PLAN_DOC_LINK,
          }}
          testId="dashboard-status-tier"
        />
        <StatusCell
          label="Product"
          help={PRODUCT_HELP}
          value={
            productStatus.kind === "resolved"
              ? productStatus.status
              : NO_DISPLAY_DATA
          }
          chipStyle={
            productStatus.kind === "resolved"
              ? productStyle[productStatus.status]
              : classes.neutral
          }
          values={productValues}
          note={{
            text: "Periscope derives this rather than reading one field, so it can disagree with Subscription Status. Active here against Canceled there means the cancellation has not flowed through yet.",
          }}
          // Our own subscription record's id, shown as a UUID because that is
          // the form the other tools around here take, with the prefixed form
          // behind the s12 chip. The id shortens to whatever room is left, so
          // both controls fit beside the pill.
          secondary={
            subscription?.subscriptionId ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                  width: "100%",
                }}
              >
                <IdValue
                  value={toUUID(subscription.subscriptionId)}
                  label="subscription UUID"
                  onCopy={copyToClipboard}
                  alternate={{
                    value: subscription.subscriptionId,
                    label: "s12",
                  }}
                  // The raw document, as the detail pane's Sub ID row offers.
                  action={
                    <Tooltip title="Open the subscription document">
                      <IconButton
                        component={RouterLink}
                        to={{
                          pathname: `/search/doc/${subscription.subscriptionId}`,
                        }}
                        target="_blank"
                        size="small"
                        color="primary"
                        aria-label="Open the subscription document"
                        sx={{ p: 0.25, flex: "none" }}
                        data-testid="dashboard-subscription-doc-link"
                      >
                        <LaunchIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  }
                />
              </Box>
            ) : undefined
          }
          wide
          testId="dashboard-status-product"
        />
        {!isFreeOrg && (
          <StatusCell
            label="Payment"
            help={PAYMENT_HELP}
            value={paymentStatus ?? "Unknown"}
            chipStyle={
              (paymentStatus && paymentStyle[paymentStatus]) || classes.neutral
            }
            values={paymentValues}
            note={{
              text: "Read verbatim from the subscription document, so a value outside this list came straight from Zuora and is worth reporting.",
            }}
            testId="dashboard-status-payment"
          />
        )}
        {!isFreeOrg && (
          <StatusCell
            label="Subscription"
            help={SUBSCRIPTION_HELP}
            value={
              state === undefined
                ? NO_DISPLAY_DATA
                : subscriptionStateLabel[state]
            }
            chipStyle={
              state === undefined
                ? classes.neutral
                : subscriptionStateStyle[state]
            }
            values={subscriptionValues}
            note={{
              text: "A \u201c(Decoupled)\u201d suffix means the subscription has no Zuora subscription ID: it exists in SafetyCulture but not in billing.",
            }}
            // This cell is billing's view of the subscription, so the identifier
            // that belongs with it is Zuora's own - the A-S number, and a way to
            // open it there.
            secondary={
              subscription?.providerSubscriptionId ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    minWidth: 0,
                    width: "100%",
                  }}
                >
                  <IdValue
                    value={subscription.providerSubscriptionId}
                    label="Zuora subscription number"
                    onCopy={copyToClipboard}
                    action={
                      zuoraSubscriptionUrl ? (
                        <Tooltip title="Open subscription in Zuora">
                          <IconButton
                            href={zuoraSubscriptionUrl}
                            target="_blank"
                            rel="noopener"
                            size="small"
                            color="primary"
                            aria-label="Open subscription in Zuora"
                            sx={{ p: 0.25 }}
                            data-testid="dashboard-open-zuora-btn"
                          >
                            <LaunchIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      ) : undefined
                    }
                  />
                </Box>
              ) : undefined
            }
            wide
            testId="dashboard-status-subscription"
          />
        )}
      </FieldGrid>
    </CardShell>
  );
};

export interface SeatsCardProps {
  refreshOrgData: () => void;
}

export const SeatsCard = ({ refreshOrgData }: SeatsCardProps) => {
  const { subscriptionSeatUsage } = useContext(SubscriptionSeatUsageContext);
  const { subData } = useContext(SubDataContext);
  const subscription = subData?.subscription;

  const isFreeOrg = isFreeTier(subscription);
  const rows = [
    { label: "Full", usage: subscriptionSeatUsage?.premiumSeatUsageSummary },
    { label: "Lite", usage: subscriptionSeatUsage?.liteSeatUsageSummary },
    {
      label: "Free",
      usage: subscriptionSeatUsage?.collaboratorSeatUsageSummary,
    },
  ];

  /**
   * Everything the subscription allocates, as a quantity.
   *
   * Only Full, Lite and Free have a purchased-against-occupied pair to meter.
   * A discount, an AI credit bundle or a sensor plan unit is a plain count, and
   * a free org has no usage summaries at all - the number that matters there is
   * what its component allocates, which is where the pane reads it from too.
   */
  const allocations = Object.entries(
    subscription?.componentAllocations ?? {},
  ).map(([key, allocation]) => {
    const pricing = allocation.allocations[0]?.pricingModel;
    return {
      label: userSeatsToString[key] ?? key,
      quantity: pricing?.case === "flat" ? (pricing.value?.quantity ?? 0) : 0,
    };
  });
  const meteredLabels = ["Full", "Lite", "Free"];
  const counted = isFreeOrg
    ? allocations
    : allocations.filter((entry) => !meteredLabels.includes(entry.label));

  return (
    <CardShell
      title="Subscription components"
      source="Org + Zuora"
      testId="dashboard-card-seats"
    >
      {/* Two columns rather than two stacked sections: what the product sold
          and what Zuora bills for are read against each other, and the card is
          half as tall side by side. They fall back to stacking when the column
          is too narrow to give the meters a usable bar. */}
      <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 5, rowGap: 1 }}>
        <Box sx={{ flex: "1 1 180px", minWidth: 160 }}>
          <SectionLabel>Product components</SectionLabel>
          {/* Seats first: they are what the card is usually opened for. The
              plain counts - a discount, a credit bundle - follow. */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {!isFreeOrg &&
              rows.map((row) => (
                <SeatMeter
                  key={row.label}
                  label={row.label}
                  used={row.usage?.occupiedSeats ?? 0}
                  purchased={row.usage?.purchasedSeats ?? 0}
                />
              ))}
            {counted.map((entry) => (
              <Box
                key={entry.label}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                data-testid={`dashboard-seat-count-${entry.label.toLowerCase()}`}
              >
                <Typography
                  sx={{
                    fontSize: 12.5,
                    fontWeight: 700,
                    color: "text.secondary",
                  }}
                >
                  {entry.label}
                </Typography>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
                  {entry.quantity.toLocaleString()}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {!isFreeOrg && (
          <Box sx={{ flex: "0 1 auto", minWidth: 130 }}>
            <SectionLabel>Zuora components</SectionLabel>
            {subscription ? (
              // The pane stacks one Typography per component, which is what
              // this column wants too. The rows stay inline rather than
              // flexing: each is "Full: 30" with a real space in it, and a
              // flex container drops the whitespace text node that makes it.
              <Box
                sx={{ display: "flex", flexDirection: "column", rowGap: 0.5 }}
              >
                <ChangeComponentQuantityZuora
                  subscription={subscription}
                  refreshOrgData={refreshOrgData}
                />
              </Box>
            ) : (
              NO_DISPLAY_DATA
            )}
          </Box>
        )}
      </Box>
    </CardShell>
  );
};

export const BillingCard = () => {
  const { subData } = useContext(SubDataContext);
  const { hasPermissions } = useContext(PermissionsContext);
  const { isFeatureFlagsLoading } = useContext(FeatureFlagsContext);
  const hasManualBillingExp = Boolean(
    UseGetFeatureFlagValue(FEATURE_FLAG_KEY_MANUAL_BILLING_EXPERIENCE),
  );
  const hasDecoupledBillingExp = Boolean(
    UseGetFeatureFlagValue(FEATURE_FLAG_KEY_DISABLE_BILLING_UPDATES),
  );
  // Same fetch and create-versus-edit decision the detail pane makes.
  const { billingDetails, statusMode, refresh } = useBillingDetails();
  const subscription = subData?.subscription;
  const isFreeOrg = isFreeTier(subscription);
  const canViewBillingContact = hasPermissions([
    ActionType.GET_ADDITIONAL_BILLING_CONTACTS,
  ]);

  const paymentMethod =
    subscription?.paymentCollectionMethod === undefined
      ? undefined
      : subscription.paymentCollectionMethod === PaymentCollectionMethod.INVOICE
        ? "Invoice"
        : subscription.paymentCollectionMethod ===
            PaymentCollectionMethod.AUTOMATIC
          ? "Automatic"
          : "Unspecified";

  return (
    <CardShell title="Billing" source="Zuora" testId="dashboard-card-billing">
      <FieldGrid>
        {/* Half each, so all three rows of this card share one column edge.
            A free org is not billed at all, so these are left out rather than
            filled with N/A - only the two forms have anything to say. */}
        {!isFreeOrg && (
          <Field label="Payment method" size="half">
            {paymentMethod || NO_DISPLAY_DATA}
          </Field>
        )}
        {!isFreeOrg && (
          <Field label="Currency" size="half">
            {subscription?.currency?.toUpperCase() || NO_DISPLAY_DATA}
          </Field>
        )}
        {!isFreeOrg && (
          <Field
            label="Billing cycle"
            size="half"
            atomic
            hint={SUBSCRIPTION_DOC_HINT}
          >
            {subscription?.billingPeriodMonths
              ? `${subscription.billingPeriodMonths} month(s)`
              : NO_DISPLAY_DATA}
          </Field>
        )}
        {!isFreeOrg && (
          <Field label="Manual billing" size="half" full={hasManualBillingExp}>
            {isFeatureFlagsLoading ? (
              <CircularProgress
                color="primary"
                variant="indeterminate"
                thickness={4}
                size={16}
                data-testid="dashboard-manual-billing-loading"
              />
            ) : hasManualBillingExp ? (
              <Alert
                severity="error"
                sx={{ textAlign: "start" }}
                data-testid="dashboard-manual-billing-warning"
              >
                This customers billing is manually managed. Please contact the
                finance team for billing inquiries.
              </Alert>
            ) : (
              <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                Not applicable
                <InfoHint title="Manual billing is for selected customers who are billed under a different commercial model. Please contact the finance team for any questions." />
              </Box>
            )}
          </Field>
        )}
        {!isFreeOrg && (
          <Field
            label="Decoupled billing"
            size="half"
            full={hasDecoupledBillingExp}
          >
            {isFeatureFlagsLoading ? (
              <CircularProgress
                color="primary"
                variant="indeterminate"
                thickness={4}
                size={16}
                data-testid="dashboard-decoupled-billing-loading"
              />
            ) : hasDecoupledBillingExp ? (
              <Alert
                severity="warning"
                sx={{ textAlign: "start" }}
                data-testid="dashboard-decoupled-billing-warning"
              >
                Billing updates are disabled for this organisation. Please
                contact the finance team for billing inquiries.
              </Alert>
            ) : (
              <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                Not applicable
                <InfoHint title="Decoupled billing applies to organisations whose billing updates are managed outside self-serve. Please contact the finance team for any questions." />
              </Box>
            )}
          </Field>
        )}
        {/* The forms carry their own dialogs and permission gates, so the card
            renders the same components the detail pane does - as bare icons
            beside the label, since the label already says which form it
            opens. */}
        <Field label="Billing details" size="half" inline>
          <BillingDetailsForm
            billingDetails={billingDetails}
            statusMode={statusMode}
            refreshBillingDetails={refresh}
            iconOnly
          />
        </Field>
        {canViewBillingContact && (
          <Field label="Billing contacts" size="half" inline>
            <BillingContactsForm iconOnly />
          </Field>
        )}
      </FieldGrid>
    </CardShell>
  );
};

export const TermCard = () => {
  const { subData } = useContext(SubDataContext);
  const subscription = subData?.subscription;

  // Every field here comes from Zuora, and a free org has no Zuora side, so
  // the card does not appear at all rather than appearing empty.
  if (isFreeTier(subscription)) return null;

  const termStart = formatDate(subscription?.termStartDate);
  const termEnd = formatDate(subscription?.termEndDate);
  const periodStart = formatDate(subscription?.currentPeriodStartedAt);
  const periodEnd = formatDate(subscription?.currentPeriodEndsAt);

  return (
    <CardShell
      title="Term Start-End"
      source="Zuora"
      testId="dashboard-card-term"
    >
      <FieldGrid>
        {/* The card's title says Start-End once, so the fields need only name
            which span they are. The pane omits these rows when the dates are
            unset; showing N/A in their place reads as missing data rather than
            "not applicable". */}
        {termStart && (
          <Field
            label="Subscription Term"
            size="half"
            atomic
            hint={SUBSCRIPTION_DOC_HINT}
          >
            <DateRange>{`${termStart} - ${termEnd ?? "Ongoing"}`}</DateRange>
          </Field>
        )}
        {periodStart && (
          <Field
            label="Billing Period"
            size="half"
            atomic
            hint={SUBSCRIPTION_DOC_HINT}
          >
            <DateRange>
              {periodEnd ? `${periodStart} - ${periodEnd}` : periodStart}
            </DateRange>
          </Field>
        )}
      </FieldGrid>
    </CardShell>
  );
};
